/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { EnterpriseLearnerInsights_MetricTimeFrame as MetricTimeframe } from '__generated__/graphql-types';

import { Divider, InlineNotification, Typography2 } from '@coursera/cds-core';
import { QuizIcon, ReadingIcon, VideoIcon } from '@coursera/cds-icons';

import LearningInsightsCard, { SubSectionName } from 'bundles/enterprise-admin-home/components/LearningInsightsCard';
import LearningInsightsRow, {
  LearningInsightsRowPlaceholder,
} from 'bundles/enterprise-admin-home/components/LearningInsightsRow';
import { useIntl } from 'bundles/page/lib/useIntl';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = {
  withMarginTop: css`
    margin-top: var(--cds-spacing-200);
  `,
  errorNotification: css`
    margin: var(--cds-spacing-300) 0;
  `,
  learnerActivityDivider: css`
    margin-bottom: var(--cds-spacing-150);
  `,
};

type Props = {
  numOfVideosWatched: number;
  numOfVideosWatchedChangePercentage: number;
  numOfReadingsCompleted: number;
  numOfReadingsCompletedChangePercentage: number;
  numOfAssessmentsTaken: number;
  numOfAssessmentsTakenChangePercentage: number;
  hasError?: boolean;
  isLoading?: boolean;
  timeframe: MetricTimeframe;
};

const HowLearnersAreLearningCard = ({
  numOfVideosWatched,
  numOfVideosWatchedChangePercentage,
  numOfReadingsCompleted,
  numOfReadingsCompletedChangePercentage,
  numOfAssessmentsTaken,
  numOfAssessmentsTakenChangePercentage,
  timeframe,
  hasError,
  isLoading,
}: Props) => {
  const intl = useIntl();

  const renderBody = () => {
    if (hasError) {
      return (
        <InlineNotification
          css={styles.errorNotification}
          severity="error"
          title={_t('There was a problem fetching learner data')}
        >
          {_t('Please try refreshing the page or attempting again at a later time.')}
        </InlineNotification>
      );
    } else if (isLoading) {
      return (
        <>
          <LearningInsightsRowPlaceholder />
          <Divider />
          <LearningInsightsRowPlaceholder />
          <Divider />
          <LearningInsightsRowPlaceholder />
        </>
      );
    } else {
      return (
        <>
          <LearningInsightsRow
            icon={VideoIcon}
            metricName={_t('Videos watched')}
            metricNum={numOfVideosWatched}
            percentageOfChange={numOfVideosWatchedChangePercentage}
            timeframe={timeframe}
            aboutMetricHeader={_t('About videos watched')}
            aboutMetricBody={
              <Typography2 component="p">
                {_t(
                  'The total number of videos that learners have watched on the platform in the selected time period.'
                )}
              </Typography2>
            }
            metricMeasurement={intl.formatMessage(
              _t('{numOfVideosWatched, plural, =0 {videos} one {video} other {videos}}'),
              { numOfVideosWatched }
            )}
          />
          <Divider />
          <LearningInsightsRow
            icon={ReadingIcon}
            metricName={_t('Readings')}
            metricNum={numOfReadingsCompleted}
            percentageOfChange={numOfReadingsCompletedChangePercentage}
            timeframe={timeframe}
            aboutMetricHeader={_t('About readings')}
            aboutMetricBody={
              <Typography2 component="p">
                {_t(
                  'The total number of readings that learners have watched on the platform in the selected time period.'
                )}
              </Typography2>
            }
            metricMeasurement={intl.formatMessage(
              _t('{numOfReadingsCompleted, plural, =0 {readings} one {reading} other {readings}}'),
              { numOfReadingsCompleted }
            )}
          />
          <Divider />
          <LearningInsightsRow
            icon={QuizIcon}
            metricName={_t('Assessments taken')}
            metricNum={numOfAssessmentsTaken}
            percentageOfChange={numOfAssessmentsTakenChangePercentage}
            timeframe={timeframe}
            aboutMetricHeader={_t('About assessments taken')}
            aboutMetricBody={
              <Typography2 component="p">
                {_t(
                  'The total number of assessments that learners have undertaken on the platform in the selected time period.'
                )}
              </Typography2>
            }
            metricMeasurement={intl.formatMessage(
              _t('{numOfAssessmentsTaken, plural, =0 {assessments} one {assessment} other {assessments}}'),
              { numOfAssessmentsTaken }
            )}
          />
        </>
      );
    }
  };

  return (
    <LearningInsightsCard
      subSectionName={SubSectionName.HowLearnersAreLearning}
      header={_t('How learners are learning')}
      timeframe={timeframe}
    >
      {renderBody()}
    </LearningInsightsCard>
  );
};

export default HowLearnersAreLearningCard;
